import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { ComplianceEntitiesSortField } from "~/graphql/supergraphEnums";
import {
  ComplianceEntitiesCountDocument,
  ComplianceEntitiesDocument,
} from "~/graphql/supergraphHooks";
import {
  type ComplianceEntitiesCountQuery,
  type ComplianceEntitiesCountQueryVariables,
  type ComplianceEntitiesQuery,
  type ComplianceEntitiesQueryVariables,
  type ComplianceEntitiesFilter,
  type ComplianceEntitiesSort,
} from "~/graphql/supergraphTypes";

// ag-grid column `colId` → server sort field. Columns absent here are not sortable
// (Investor program has no backing sort field on the slice).
const SORT_FIELD_BY_COL_ID: Record<string, ComplianceEntitiesSortField> = {
  name: ComplianceEntitiesSortField.EntityName,
  status: ComplianceEntitiesSortField.Status,
  productLine: ComplianceEntitiesSortField.ProductLine,
  entityProgram: ComplianceEntitiesSortField.EntityProgram,
  onboardingStatus: ComplianceEntitiesSortField.OnboardingStatus,
};

// The checkbox for entities with no program is shown as "--" and sent as null, matching the
// pre-onboarding grid (AssignProgramTable maps "--" to null the same way).
const UNASSIGNED_PROGRAM = "--";

// Investor program is filterable but not sortable: the cell composes four signals, so there is
// no single field to order by, but the compliance program within it can be matched (COMP-5056).
// The slice's filter `field` is a plain string, so this needs no enum member.
const FILTER_FIELD_BY_COL_ID: Record<string, string> = {
  ...SORT_FIELD_BY_COL_ID,
  investorProgram: "COMPLIANCE_PROGRAM",
};

// A text filter contributes its `filter` string; a set filter its selected `values`.
const filterValuesFromModel = (model: unknown): (string | null)[] => {
  const set = model as { filterType?: string; values?: unknown[]; filter?: string };
  if (set.filterType === "set") {
    return (set.values ?? [])
      .filter((v): v is string => typeof v === "string")
      .map((v) => (v === UNASSIGNED_PROGRAM ? null : v));
  }
  return typeof set.filter === "string" ? [set.filter] : [];
};

// Maps each column's ag-grid filter model to a slice filter directive.
const getFilters = (
  filterModel: AgGrid.IServerSideGetRowsRequest["filterModel"],
): ComplianceEntitiesFilter[] | null => {
  if (!filterModel || Array.isArray(filterModel)) {
    return null;
  }
  const filters = Object.entries(filterModel)
    .map(([colId, model]) => ({
      field: FILTER_FIELD_BY_COL_ID[colId],
      filterValues: filterValuesFromModel(model),
    }))
    // An empty selection is sent, not dropped: for a checkbox column the slice reads it as
    // "no rows match", and dropping it would widen the grid back to every row (COMP-5056).
    // ag-grid removes the model entirely when everything is selected, so "all" still means
    // "unfiltered" without the column having to enumerate its own values.
    .filter((f): f is { field: string; filterValues: (string | null)[] } => Boolean(f.field))
    .map((f) => ({ field: f.field, filterValues: f.filterValues }));
  return filters.length > 0 ? filters : null;
};

const getSort = (sortModel: AgGrid.SortModelItem[]): ComplianceEntitiesSort | null => {
  // The slice takes a single sort directive; ag-grid gives an array, so take the first.
  const first = sortModel?.[0];
  if (!first) {
    return null;
  }
  const field = SORT_FIELD_BY_COL_ID[first.colId];
  if (!field) {
    return null;
  }
  return { field, sortAscending: first.sort === "asc" };
};

/**
 * AG Grid SSRM datasource for the Entities tab, backed by `complianceEntitiesSlice`.
 * The slice is scoped to a single client (`arenaId`, chosen via the search gate), paginated
 * with offset/limit, and server-sorted. The total row count comes from a separate
 * `complianceEntitiesSliceCount` query (cached per arena — it does not change between pages).
 */
export class ComplianceEntitiesGraphqlDataSource implements AgGrid.IServerSideDatasource {
  private client: ApolloClient<NormalizedCacheObject>;
  private arenaId: number;
  private totalCount: number | null = null;
  // The count changes with the filter set, so the cache is keyed on it
  private totalCountFilterKey: string | null = null;

  constructor(arenaId: number) {
    this.client = client;
    this.arenaId = arenaId;
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const offset = params.request.startRow ?? 0;
    const limit = (params.request.endRow ?? 0) - offset;
    const sort = getSort(params.request.sortModel);
    const filters = getFilters(params.request.filterModel);
    const filterKey = JSON.stringify(filters);

    const rowsPromise = this.client.query<
      ComplianceEntitiesQuery,
      ComplianceEntitiesQueryVariables
    >({
      query: ComplianceEntitiesDocument,
      variables: { arenaId: this.arenaId, offset, limit, sort, filters },
      fetchPolicy: "no-cache",
    });

    const countPromise: Promise<number> =
      this.totalCount !== null && this.totalCountFilterKey === filterKey
        ? Promise.resolve(this.totalCount)
        : this.client
            .query<ComplianceEntitiesCountQuery, ComplianceEntitiesCountQueryVariables>({
              query: ComplianceEntitiesCountDocument,
              variables: { arenaId: this.arenaId, filters },
              fetchPolicy: "no-cache",
            })
            .then((res) => res.data.complianceEntitiesSliceCount);

    Promise.all([rowsPromise, countPromise])
      .then(([rowsRes, count]) => {
        this.totalCount = count;
        this.totalCountFilterKey = filterKey;
        params.success({
          rowData: rowsRes.data.complianceEntitiesSlice,
          rowCount: count,
        });
      })
      .catch(() => {
        params.fail();
      });
  }
}
